import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { notificationService } from "../services/notificationService";

interface Props {
  userEmail: string;
  displayName?: string;
}

interface UserSettings {
  notifications: boolean;
  autoJoinOrders: boolean;
  preferredRestaurants: string[];
  theme: 'dark' | 'light';
}

export default function ProfilePage({ userEmail, displayName }: Props) {
  const [settings, setSettings] = useState<UserSettings>({
    notifications: true,
    autoJoinOrders: false,
    preferredRestaurants: [],
    theme: 'dark'
  });

  const [newRestaurant, setNewRestaurant] = useState("");
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [requestingPermission, setRequestingPermission] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('userNotificationSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error parsing saved settings:', error);
      }
    }

    // Check current notification permission
    if (notificationService.isSupported()) {
      setNotificationPermission(notificationService.getPermission());
    }
  }, []);

  const handleSettingChange = (key: keyof UserSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // Save to localStorage
    notificationService.setEnabledInSettings(newSettings.notifications);
  };

  const handleRequestNotificationPermission = async () => {
    if (!notificationService.isSupported()) {
      toast.error('Trình duyệt này không hỗ trợ thông báo');
      return;
    }

    setRequestingPermission(true);
    try {
      const permission = await notificationService.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        // Show test notification
        notificationService.showNotification(
          '✅ Thông báo đã được bật!',
          {
            body: 'Bạn sẽ nhận được thông báo khi có order mới',
            icon: '🔔'
          }
        );
      } else if (permission === 'denied') {
        toast.error('Thông báo đã bị từ chối. Vui lòng bật thông báo trong cài đặt trình duyệt.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setRequestingPermission(false);
    }
  };

  const getNotificationStatus = () => {
    if (!notificationService.isSupported()) {
      return { text: 'Không hỗ trợ', color: 'text-red-400', canRequest: false };
    }
    
    switch (notificationPermission) {
      case 'granted':
        return { text: 'Đã cho phép', color: 'text-emerald-400', canRequest: false };
      case 'denied':
        return { text: 'Đã từ chối', color: 'text-red-400', canRequest: false };
      default:
        return { text: 'Chưa thiết lập', color: 'text-yellow-400', canRequest: true };
    }
  };

  const addPreferredRestaurant = () => {
    if (newRestaurant.trim()) {
      setSettings(prev => ({
        ...prev,
        preferredRestaurants: [...prev.preferredRestaurants, newRestaurant.trim()]
      }));
      setNewRestaurant("");
    }
  };

  const removePreferredRestaurant = (index: number) => {
    setSettings(prev => ({
      ...prev,
      preferredRestaurants: prev.preferredRestaurants.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-4">
            <span className="text-3xl">👤</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Thông tin cá nhân
          </h1>
          <p className="text-slate-300 text-xl">
            Quản lý tài khoản và cài đặt
          </p>
        </header>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* User Info */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span>ℹ️</span>
              Thông tin tài khoản
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/50 flex items-center justify-center">
                  <span className="text-2xl">👤</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{displayName || 'Người dùng'}</h3>
                  <p className="text-slate-400">{userEmail}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span>⚙️</span>
              Cài đặt
            </h2>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-700/50 border border-slate-600/30">
                  <div>
                    <h4 className="text-white font-medium">Thông báo</h4>
                    <p className="text-slate-400 text-sm">Nhận thông báo về orders mới</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications}
                      onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>

                {/* Chrome Notification Permission */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-700/50 border border-slate-600/30">
                  <div className="flex-1">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      🔔 Quyền thông báo Chrome
                    </h4>
                    <p className="text-slate-400 text-sm">
                      Trạng thái: <span className={`font-medium ${getNotificationStatus().color}`}>
                        {getNotificationStatus().text}
                      </span>
                    </p>
                  </div>
                  {getNotificationStatus().canRequest && (
                    <button
                      onClick={handleRequestNotificationPermission}
                      disabled={requestingPermission}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        requestingPermission
                          ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
                      }`}
                    >
                      {requestingPermission ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></div>
                          Đang yêu cầu...
                        </div>
                      ) : (
                        '✋ Yêu cầu quyền'
                      )}
                    </button>
                  )}
                </div>
              </div>
 
            </div>
          </div>

        </div>
      </div>
  );
}
